<template>
  <div
    class="print-modal modal"
  >
    <button
      type="button"
      class="upper-close"
      @click="closeOverlay"
      :aria-label="$t('global.close.button')"
    />
    <div
      class="inner-container"
    >
      <img
        class="thumbnail"
        :src="productDetails.images[0].thumbnail"
        :alt="productDetails.product.name"
        width="150"
        height="150"
        loading="lazy"
      >
      <h1 class="modal-headline-mobile">
        {{ productDetails.product.name }}
      </h1>
      <div
        class="print-details"
      >
        <h1 class="modal-headline">
          {{ productDetails.product.name }}
        </h1>
        <div
          class="inner-print-details"
        >
          <Form
            id="print-form"
            :initial-values="initialValues"
            ref="printForm"
            @submit="addToCart"
            @invalid-submit="onInvalidSubmit"
            v-slot="context"
          >
            <csrf-input
              ref="csrf"
            />
            <div
              v-if="!productDetails.printDetails.printableWithoutColor"
            >
              <span
                class="section-header"
              >
                {{ $t('ads.main.print.header.grounding') }}
              </span>
              <p>
                {{ $t('ads.main.print.text.grounding') }}
              </p>
              <div
                class="color-codes"
                id="color-codes-identifier"
              >
                <input-vee4
                  :css-class="'code-input'"
                  name="cyan"
                  label="C"
                  rules="required|min_value:0|max_value:100"
                  :suppress-error-messages="true"
                  id="input-vue-text-cyan"
                  @keyUpEvent="colorComponentChanged"
                />

                <input-vee4
                  :css-class="'code-input'"
                  name="magenta"
                  label="M"
                  rules="required|min_value:0|max_value:100"
                  :suppress-error-messages="true"
                  @keyUpEvent="colorComponentChanged"
                />

                <input-vee4
                  :css-class="'code-input'"
                  name="yellow"
                  label="Y"
                  rules="required|min_value:0|max_value:100"
                  :suppress-error-messages="true"
                  @keyUpEvent="colorComponentChanged"
                />

                <input-vee4
                  :css-class="'code-input'"
                  name="key"
                  label="K"
                  rules="required|min_value:0|max_value:100"
                  :suppress-error-messages="true"
                  @keyUpEvent="colorComponentChanged"
                />
              </div>

              <div
                v-if="context.errors?.cyan || context.errors?.magenta || context.errors?.yellow || context.errors?.key"
                class="input__error | input-error"
                v-html="context.errors?.cyan ?? context.errors?.magenta ?? context.errors?.yellow ?? context.errors?.key"
              />

              <div class="color-palette-container">
                <button
                  type="button"
                  v-for="(color, index) in productDetails.printDetails.colors"
                  :key="index"
                  :id="'color_'+color.rankOrder"
                  class="color-palette"
                  :class="{'palette-active': color.rankOrder === lastClickedColor?.rankOrder}"
                  :style="{'background': `#${color.hexEquivalent}`}"
                  :aria-label="$t('global.print.color.aria.label',[color.hexEquivalent])"
                  @click="setColor(color)"
                />

                <button
                  type="button"
                  class="color-palette colorless-palette"
                  :class="{'palette-active': 'customCYMK' === lastClickedColor}"
                  @click="setColor('customCYMK')"
                >
                  {{ $t('ads.main.print.colors.code') }}
                </button>
              </div>

              <div class="print-separator-container">
                <div
                  class="print-separator"
                >
                  <div
                    class="print-separator-line"
                  />
                </div>
              </div>
            </div>
            <span
              class="section-header"
            >
              {{ $t('ads.main.print.header.individualFiles') }}
            </span>
            <p>
              {{ $t('ads.main.print.text.individualFiles') }}
            </p>
            <span class="section-header">
              {{ $t('ads.main.print.header.firstFile') }}</span>
            <div class="size-inputs">
              <label
                class="size-button"
                for="radio-one-small"
                tabindex="0"
              >
                <Field
                  name="sizeOne"
                  id="radio-one-small"
                  type="radio"
                  value="small"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.small') }}
              </label>

              <label
                class="size-button"
                for="radio-one-medium"
                tabindex="0"
              >
                <Field
                  name="sizeOne"
                  id="radio-one-medium"
                  type="radio"
                  value="medium"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.medium') }}
              </label>

              <label
                class="size-button"
                for="radio-one-large"
                tabindex="0"
              >
                <Field
                  name="sizeOne"
                  id="radio-one-large"
                  type="radio"
                  value="large"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.large') }}
              </label>
            </div>

            <image-dropzone
              name="fileOne"
              :rules="{required: !context.values.fileTwo, ext: extensions, size: $t('ads.main.dropzone.size'),}"
              ref="dropzoneOne"
              @change="({file, error}) => {
                if (error === 'size' || error === 'ext') {
                  context.setFieldError('fileOne', $t('validation.print.invalidFile'))
                  return
                }

                if (file) {
                  context.setFieldValue('fileOne', file)
                }
              }"
            />

            <span
              class="section-header"
            >{{ $t('ads.main.print.header.secondFile') }}</span>
            <div class="size-inputs">
              <label
                class="size-button"
                for="radio-two-small"
                tabindex="0"
              >
                <Field
                  name="sizeTwo"
                  id="radio-two-small"
                  type="radio"
                  value="small"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.small') }}
              </label>

              <label
                class="size-button"
                for="radio-two-medium"
                tabindex="0"
              >
                <Field
                  name="sizeTwo"
                  id="radio-two-medium"
                  type="radio"
                  value="medium"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.medium') }}
              </label>

              <label
                class="size-button"
                for="radio-two-large"
                tabindex="0"
              >
                <Field
                  name="sizeTwo"
                  id="radio-two-large"
                  type="radio"
                  value="large"
                  class="radio-input__input"
                />
                {{ $t('ads.main.print.image.size.button.large') }}
              </label>
            </div>

            <image-dropzone
              name="fileTwo"
              :rules="{required: !context.values.fileOne, ext: extensions, size: $t('ads.main.dropzone.size'),}"
              ref="dropzoneTwo"
              @change="({file, error}) => {
                if (error === 'size' || error === 'ext') {
                  context.setFieldError('fileTwo', $t('validation.print.invalidFile'))
                  return
                }

                if (file) {
                  context.setFieldValue('fileTwo', file)
                }
              }"
            />

            <div
              class="print-separator-container"
            >
              <div
                class="print-separator"
              >
                <div
                  class="print-separator-line"
                />
              </div>
            </div>

            <div
              class="form__group"
            >
              <checkbox-vee4
                name="tos"
                :label="$t('global.tosAndPrivacy.label')"
                rules="required"
              />
            </div>

            <div class="button-submit">
              <button
                type="submit"
                class="sale-box__button-to-cart sale-box__button-to-cart--printModal | button button--alt button--add-to-cart"
                :class="{'button-with-loader':uploading}"
                :disabled="uploading"
              >
                <loader
                  v-if="uploading"
                  class="accordion__loader print_loader"
                />
                <span>
                  {{ $t(`ads.main.print.submit.button.${uploading ? 'uploading' : 'notUploading'}`) }}
                </span>
              </button>
            </div>
          </Form>
        </div>
      </div>
      <div
        class="border-right"
      />
    </div>
  </div>
</template>

<script>
import productPageStateComputedMixin
  from '../../utilities/vueMixins/productPageStateComputedMixin/productPageStateComputedMixin.js'
import ImageDropzone from '../imageDropzone/ImageDropzone'
import FormMixin from '../../utilities/vueMixins/formMixin/formMixin'
import CsrfInput from '../csrfInput/csrfInput'
import Loader from '../loader/loader'
import {breakpoints} from '../../variables/variables.js'
import {Field, Form} from 'vee-validate'
import InputVee4 from '../inputVue/inputVee4.vue'
import CheckboxVee4 from '../checkbox/checkboxVee4.vue'
import {nextTick} from 'vue'
import scrollToElement from '../../utilities/js/scrollToElement/scrollToElement'

export default {
  components: {
    Field,
    CheckboxVee4,
    InputVee4,
    Form,
    ImageDropzone,
    CsrfInput,
    Loader
  },
  mixins: [
    productPageStateComputedMixin,
    FormMixin
  ],
  props: {
    getFormData: Function,
    clickOnCartHandler: Function
  },
  data() {
    return {
      extensions: this.$t('ads.main.print.rules.ext').split(','),
      lastClickedColor: 'customCYMK',
      uploading: false,
      initialValues: null,
    }
  },
  created() {
    const c = this.productDetails.printDetails?.colors?.[0]
    this.lastClickedColor = c ?? 'customCYMK'

    this.initialValues = {
      cyan: c?.cyan ?? 0,
      magenta: c?.magenta ?? 0,
      yellow: c?.yellow ?? 0,
      key: c?.key ?? 0,
      sizeOne: 'medium',
      fileOne: undefined,
      sizeTwo: 'medium',
      fileTwo: undefined,
      tos: false
    }
  },
  mounted() {
    this.$store.state.isOverlayVisible = true
    this.$nextTick(() => {
      document.querySelectorAll('input[type="file"]:not([aria-label])').forEach((input, index) => {
        input.setAttribute("aria-label", `Upload file ${index + 1}`);
      });
    });
  },
  emits: ['success', 'error', 'cancel'],
  methods: {
    closeOverlay() {
      this.$emit('cancel')
    },
    setColor(color) {
      if (typeof color === 'object') {
        this.lastClickedColor = color
        this.$refs.printForm.setValues({
          cyan: color.cyan,
          magenta: color.magenta,
          yellow: color.yellow,
          key: color.key,
        })
      } else if (typeof color === 'string') {
        if (this.lastClickedColor !== 'customCYMK') {
          this.$refs.printForm.setValues({
            cyan: 0,
            magenta: 0,
            yellow: 0,
            key: 0,
          })
          this.lastClickedColor = 'customCYMK'
        }

        nextTick(() => {
          if (window.innerWidth < breakpoints.phoneLg) {
            const firstInvalidInput = document.getElementById('color-codes-identifier')
            if (firstInvalidInput) {
              scrollToElement(firstInvalidInput, 20)
            }
          }

          document.getElementById('input-vue-text-cyan').focus()
        })
      }
    },
    colorComponentChanged() {
      nextTick(() => {
        this.lastClickedColor = 'customCYMK'
      })
    },
    onInvalidSubmit(context) {
      let firstInvalidInput
      if (!context.results.cyan.valid) {
        firstInvalidInput = this.$el.querySelector('input[name="cyan"]')
      } else if (!context.results.magenta.valid) {
        firstInvalidInput = this.$el.querySelector('input[name="magenta"]')
      } else if (!context.results.yellow.valid) {
        firstInvalidInput = this.$el.querySelector('input[name="yellow"]')
      } else if (!context.results.key.valid) {
        firstInvalidInput = this.$el.querySelector('input[name="key"]')
      } else if (!context.results.fileOne.valid) {
        firstInvalidInput = this.$refs.dropzoneOne.$el
      } else if (!context.results.fileTwo.valid) {
        firstInvalidInput = this.$refs.dropzoneTwo.$el
      } else if (!context.results.tos.valid) {
        firstInvalidInput = this.$el.querySelector('input[name="tos"]')
      } else {
        return
      }

      if (firstInvalidInput) {
        scrollToElement(firstInvalidInput, 20)
      }

    },
    addToCart(values) {
      if (!this.uploading) {
        let formData = new FormData()

        let data = this.getFormData()
        for (let key in data) {
          formData.append(key, data[key])
        }

        for (let key in values) {
          if (key === 'tos') {
            continue
          }

          formData.append(key, values[key])
        }

        this.uploading = true
        this.$store.dispatch('productPage/addToCartWithPrint', {formData: formData, url: this.addWithPrintToBasketUrl})
          .then(() => {
            this.$emit('success')
          })
          .catch(error => {
            this.uploading = false
            this.$emit('error', {message: error.response.data})
          })
      }
    },
  }
}

</script>
